<template>
  <a-input-number
    v-model:value="innerValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :size="size"
    :min="min"
    :max="max"
    :step="step"
    :precision="precision"
    :controls="controls"
    :status="status"
    :formatter="formatter"
    :parser="parser"
    class="ant-number-input"
    @blur="onBlur"
    @pressEnter="onPressEnter"
  />
</template>

<script lang="ts" setup>
import { computed } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";

interface Props {
  modelValue?: number | null;
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
  controls?: boolean;
  status?: "" | "error" | "warning";
  formatter?: (value: number | string, info: { userTyping: boolean; input: string }) => string;
  parser?: (displayValue: string | undefined) => number | string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  placeholder: "Nhập số",
  disabled: false,
  size: "middle",
  min: undefined,
  max: undefined,
  step: 1,
  precision: undefined,
  controls: true,
  status: "",
  formatter: undefined,
  parser: undefined,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: number | null): void;
  (e: "blur", event: FocusEvent): void;
  (e: "pressEnter", event: KeyboardEvent): void;
  (e: "change", value: number | null): void;
}>();

const innerValue = computed({
  get: () => props.modelValue,
  set: (val: number | null) => {
    emit("update:modelValue", val);
    emit("change", val);
  },
});

const onBlur = (e: FocusEvent) => emit("blur", e);
const onPressEnter = (e: KeyboardEvent) => emit("pressEnter", e);
</script>

<style scoped>
.ant-number-input {
  width: 100%;
}
</style>

<!--
  Hướng dẫn sử dụng — Number Input
  Global: <h-inputNumber v-model="qty" :min="1" :max="100" />
  Import: import { InputNumberDefault } from '@/components/ant/input'

  Props: modelValue, placeholder, disabled, size, min, max, step, precision, controls, status
  Events: update:modelValue, change, blur, pressEnter
  Docs: src/components/ant/input/README.md
-->
